import type { IconName } from '../components/icons/Icon';

/**
 * Single source of truth for the console's information architecture.
 *
 * The previous navigation inlined five near-identical `.map()` blocks inside
 * SuperAdminNavigation, one per section, so adding a tab meant copying a
 * 20-line button template and the SonarQube tab ended up reachable only from a
 * third, redundant tab strip rendered inside SysDevOpsSection. Describing the
 * IA as data instead means the sidebar, the sub-tab strip, the breadcrumb, the
 * page header and the command palette all read the same tree.
 *
 * Section and tab `key`s are deliberately unchanged from the previous
 * navigation: every downstream section component switches on them, so this is
 * a presentation and structure change, not a routing rewrite.
 */

export interface NavTab {
  key: string;
  label: string;
  /** One line, used by the page header and the command palette. */
  description: string;
  icon: IconName;
}

export interface NavSection {
  key: string;
  label: string;
  description: string;
  icon: IconName;
  /**
   * Which tab a section opens on. Previously every section was forced to
   * 'overview' on click, but Sys & Dev Ops has no overview — it silently
   * remapped 'overview' to 'builds' inside the section body.
   */
  defaultTab: string;
  tabs: NavTab[];
}

export interface NavGroup {
  /** Group headings turn five flat peers into three scannable clusters. */
  label: string;
  sections: NavSection[];
}

const trafficDrivers: NavSection = {
  key: 'traffic-drivers',
  label: 'Traffic Drivers',
  description: 'QR codes, affiliates, and the channels bringing people in',
  icon: 'growth',
  defaultTab: 'overview',
  tabs: [
    { key: 'overview', label: 'Overview', description: 'Scan, click, and conversion totals at a glance', icon: 'overview' },
    { key: 'affiliates', label: 'Affiliates', description: 'Views, orders, and commission owed per affiliate', icon: 'affiliates' },
    { key: 'recruit', label: 'Recruit', description: 'Add a Chill Setter and issue their link and code', icon: 'name' },
    { key: 'qr-tracking', label: 'QR Tracking', description: 'Create, print, and track individual QR codes', icon: 'qr' },
    { key: 'analytics', label: 'Analytics', description: 'Channel effectiveness and conversion rates', icon: 'analytics' },
    { key: 'referrals', label: 'Referrals', description: 'Referral queue, attribution, and payout state', icon: 'referrals' },
    { key: 'mailing-list', label: 'Mailing List', description: 'Who asked to hear when the bottles ship, and where they signed up', icon: 'inbox' },
  ],
};

const sales: NavSection = {
  key: 'sales',
  label: 'Sales & Transactions',
  description: 'Orders, payments, customers, and refunds',
  icon: 'sales',
  defaultTab: 'overview',
  tabs: [
    { key: 'overview', label: 'Overview', description: 'Revenue, order volume, and average order value', icon: 'overview' },
    { key: 'orders', label: 'Orders', description: 'Every order and its fulfilment state', icon: 'orders' },
    { key: 'sales', label: 'Sales Analytics', description: 'Revenue trends and product mix over time', icon: 'analytics' },
    { key: 'transactions', label: 'Transactions', description: 'Individual Stripe payments and their status', icon: 'transactions' },
    { key: 'customers', label: 'Customers', description: 'Who is buying, and how often they come back', icon: 'customers' },
    { key: 'refunds', label: 'Refunds', description: 'Refunded orders and the reasons given', icon: 'refunds' },
    { key: 'shipping', label: 'Shipping', description: 'Rates, labels, and fulfilment status per order', icon: 'shipping' },
  ],
};

const productManagement: NavSection = {
  key: 'product-management',
  label: 'Products',
  description: 'Catalogue, pricing, and product performance',
  icon: 'products',
  defaultTab: 'overview',
  tabs: [
    { key: 'overview', label: 'Overview', description: 'Catalogue size, stock, and top sellers', icon: 'overview' },
    { key: 'products', label: 'Catalogue', description: 'Add, edit, and retire products', icon: 'products' },
    { key: 'batches', label: 'Batches', description: 'Production runs, what is left, and what checkout will still sell', icon: 'inbox' },
    { key: 'pricing', label: 'Pricing', description: 'Price points, discounts, and margin', icon: 'pricing' },
    { key: 'analytics', label: 'Analytics', description: 'Units sold and revenue per product', icon: 'analytics' },
  ],
};

const instagramAgent: NavSection = {
  key: 'instagram-agent',
  label: 'Instagram Agent',
  description: 'AI-generated posts, approvals, and paid campaigns',
  icon: 'instagram',
  defaultTab: 'overview',
  tabs: [
    { key: 'overview', label: 'Overview', description: 'Approval queue, publishing volume, and budget', icon: 'overview' },
    { key: 'content-studio', label: 'Content Studio', description: 'Generate, review, and schedule posts', icon: 'content' },
    { key: 'campaigns', label: 'Campaigns', description: 'Paid campaigns and their spend', icon: 'campaign' },
    { key: 'analytics', label: 'Analytics', description: 'Reach, engagement, and follower growth', icon: 'analytics' },
    { key: 'settings', label: 'Settings', description: 'Account connection, budget caps, and brand voice', icon: 'settings' },
  ],
};

const sysDevOps: NavSection = {
  key: 'sys-devops',
  label: 'Sys & Dev Ops',
  description: 'Builds, quality gates, security, and deployments',
  icon: 'devops',
  // No 'overview' tab exists for this section; opening on Builds is what the
  // section body already did behind the scenes.
  defaultTab: 'builds',
  tabs: [
    { key: 'builds', label: 'Builds', description: 'Test runs and build results', icon: 'build' },
    { key: 'coverage', label: 'Coverage', description: 'Test coverage by package', icon: 'coverage' },
    { key: 'quality', label: 'Quality', description: 'Lint and code quality signals', icon: 'quality' },
    // Key stays 'sonarqube': it is in URLs and bookmarks, and the section
    // component switches on it. The label is what people read.
    { key: 'sonarqube', label: 'Static Code Testing', description: 'Static analysis findings and quality gate', icon: 'quality' },
    { key: 'security', label: 'Security', description: 'Dependency and code security findings', icon: 'security' },
    { key: 'system', label: 'System', description: 'Service health and resource usage', icon: 'system' },
    { key: 'deployments', label: 'Deployments', description: 'Staging and production release history', icon: 'deploy' },
    { key: 'secrets', label: 'Secrets', description: 'Runtime credentials per environment', icon: 'security' },
    { key: 'database', label: 'Database', description: 'Browse the table, its entities, and its metrics', icon: 'system' },
  ],
};

export const NAV_GROUPS: NavGroup[] = [
  { label: 'Commerce', sections: [sales, productManagement] },
  { label: 'Growth', sections: [trafficDrivers, instagramAgent] },
  { label: 'Platform', sections: [sysDevOps] },
];

export const NAV_SECTIONS: NavSection[] = NAV_GROUPS.flatMap((group) => group.sections);

export const DEFAULT_SECTION_KEY = sales.key;

export function findSection(sectionKey: string): NavSection | undefined {
  return NAV_SECTIONS.find((section) => section.key === sectionKey);
}

export function findTab(sectionKey: string, tabKey: string): NavTab | undefined {
  return findSection(sectionKey)?.tabs.find((tab) => tab.key === tabKey);
}

/**
 * Resolves an arbitrary section/tab pair — from a URL, a stale bookmark, or a
 * section switch — to a pair that actually exists.
 */
export function resolveRoute(
  sectionKey: string | undefined,
  tabKey: string | undefined
): { section: NavSection; tab: NavTab } {
  const section = (sectionKey && findSection(sectionKey)) || findSection(DEFAULT_SECTION_KEY)!;
  const tab =
    (tabKey && section.tabs.find((candidate) => candidate.key === tabKey)) ||
    section.tabs.find((candidate) => candidate.key === section.defaultTab) ||
    section.tabs[0];
  return { section, tab };
}

/** Flattened section+tab pairs, for the command palette's fuzzy search. */
export interface NavDestination {
  sectionKey: string;
  sectionLabel: string;
  tabKey: string;
  tabLabel: string;
  description: string;
  icon: IconName;
  group: string;
}

export const NAV_DESTINATIONS: NavDestination[] = NAV_GROUPS.flatMap((group) =>
  group.sections.flatMap((section) =>
    section.tabs.map((tab) => ({
      sectionKey: section.key,
      sectionLabel: section.label,
      tabKey: tab.key,
      tabLabel: tab.label,
      description: tab.description,
      icon: tab.icon,
      group: group.label,
    }))
  )
);
